import React from 'react';

import useMountAnimation from '../../../../../hooks/useMountAnimation';
import { MainSlide, type StepSlideDir } from './WizardLayout.styles';

/**
 * Module-level memory of the last step index. Each wizard step is its own route,
 * so the layout REMOUNTS on navigation — a freshly-mounted instance has no
 * previous state to diff against. Keeping the last index at module scope lets the
 * new instance still tell forward (a later step) from back (an earlier step) and
 * slide the incoming preview in from the correct side. `-1` until the first step
 * mounts, so the very first paint just fades (no slide).
 */
let lastStepIndex = -1;

/**
 * Slides the wizard's main viewport (the left preview pane) IN on each desktop
 * step change: forward navigations arrive from the right, back from the left, and
 * the first paint just fades. Slide-IN only — the outgoing step unmounts with its
 * route, so there is no leaving layer to animate (the chosen lightweight
 * behaviour). The sidebar/chrome stay put.
 *
 * Lives inside `WizardLayout`, which only renders on desktop (mobile uses the
 * separate `MobileApp`), so the effect is desktop-scoped by construction.
 */
export const MainViewportSlide = ({
  step,
  children,
}: {
  step: number;
  children: React.ReactNode;
}): React.ReactElement => {
  // Direction is decided once, at this mount's first render — before the effect
  // below advances the module memory — and frozen for the step's whole life, so an
  // in-step re-render (e.g. focusing another feature, which swaps the preview's
  // content) never replays the entrance.
  const dirRef = React.useRef<StepSlideDir | null>(null);
  if (dirRef.current === null) {
    dirRef.current =
      lastStepIndex < 0 || step === lastStepIndex ? 'none' : step > lastStepIndex ? 'forward' : 'back';
  }

  // First-paint at the travel offset (opacity 0), then flip to rest next frame so
  // the CSS transition animates it in — the area's standard entrance pattern.
  const entered = useMountAnimation();

  // Remember this step so the NEXT step's fresh mount can derive its direction.
  React.useEffect(() => {
    lastStepIndex = step;
  }, [step]);

  return (
    <MainSlide $dir={dirRef.current} $entered={entered}>
      {children}
    </MainSlide>
  );
};

export default MainViewportSlide;
